<script lang="ts">
	// Create / edit a chat folder (SAY-1820): a name plus optional custom
	// instructions, which the agent appends to the operating prompt of every
	// chat started inside the folder (like Ollama's folder system prompt).
	import { tick } from 'svelte';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import Spinner from './Spinner.svelte';
	import { toast } from 'svelte-sonner';
	import { createFolder, updateFolder, type ChatFolder } from '$lib/folders-store.svelte';

	let {
		open = $bindable(false),
		folder = null,
		onSaved,
	}: {
		open?: boolean;
		/** null = create a new folder. */
		folder?: ChatFolder | null;
		onSaved?: (folder: ChatFolder) => void;
	} = $props();

	let name = $state('');
	let instructions = $state('');
	let saving = $state(false);
	let nameEl = $state<HTMLInputElement | undefined>(undefined);

	$effect(() => {
		if (!open) return;
		name = folder?.name ?? '';
		instructions = folder?.instructions ?? '';
		void tick().then(() => nameEl?.focus());
	});

	async function save(): Promise<void> {
		const n = name.trim();
		if (!n || saving) return;
		saving = true;
		try {
			const saved = folder
				? await updateFolder(folder.id, { name: n, instructions })
				: await createFolder({ name: n, instructions });
			open = false;
			onSaved?.(saved);
		} catch (err) {
			toast.error(folder ? 'Could not save the folder' : 'Could not create the folder', {
				description: err instanceof Error ? err.message : String(err),
			});
		} finally {
			saving = false;
		}
	}
</script>

<Dialog.Root bind:open>
	<Dialog.Content
		class="w-[34rem] max-w-[calc(100%-1rem)] gap-0 rounded-3xl border border-white bg-white px-5 py-5 dark:border-gray-900 dark:bg-gray-950"
		showCloseButton={false}
		data-testid="folder-dialog"
	>
		<Dialog.Header class="gap-0 text-left">
			<Dialog.Title class="mb-1 text-sm font-medium text-gray-900 dark:text-gray-200">
				{folder ? 'Edit folder' : 'New folder'}
			</Dialog.Title>
			<Dialog.Description class="text-xs text-gray-500">
				Chats started in this folder follow its instructions.
			</Dialog.Description>
		</Dialog.Header>
		<form
			class="mt-4 flex flex-col gap-3"
			onsubmit={(e) => {
				e.preventDefault();
				void save();
			}}
		>
			<label class="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
				Name
				<input
					bind:this={nameEl}
					bind:value={name}
					maxlength="80"
					required
					placeholder="e.g. Amazon Ads"
					class="h-8 rounded-lg bg-gray-100 px-2.5 text-sm text-gray-900 outline-hidden placeholder:text-gray-400 dark:bg-gray-850 dark:text-gray-100"
					data-testid="folder-name"
				/>
			</label>
			<label class="flex flex-col gap-1 text-xs text-gray-600 dark:text-gray-400">
				Instructions <span class="font-normal text-gray-400">(optional system prompt for chats in this folder)</span>
				<textarea
					bind:value={instructions}
					maxlength="4000"
					rows="5"
					placeholder="e.g. Answer in German. Always start with the ACOS of the last 7 days."
					class="resize-y rounded-lg bg-gray-100 px-2.5 py-2 text-sm text-gray-900 outline-hidden placeholder:text-gray-400 dark:bg-gray-850 dark:text-gray-100"
					data-testid="folder-instructions"
				></textarea>
			</label>
			<div class="mt-2 flex justify-between gap-1.5">
				<button
					type="button"
					class="flex h-7 w-full items-center justify-center rounded-lg bg-gray-100 px-2.5 text-xs text-gray-800 transition hover:bg-gray-100/70 disabled:opacity-60 dark:bg-gray-850 dark:text-white dark:hover:bg-gray-850/60"
					onclick={() => (open = false)}
					disabled={saving}
				>
					Cancel
				</button>
				<button
					type="submit"
					class="flex h-7 w-full items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-2.5 text-xs text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
					disabled={saving || !name.trim()}
					data-testid="folder-save"
				>
					{#if saving}<Spinner class="size-3" />{/if}
					{folder ? 'Save' : 'Create folder'}
				</button>
			</div>
		</form>
	</Dialog.Content>
</Dialog.Root>
